import { Component, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { environment } from 'src/environments/environment';

interface ExpirationOption {
  label: string;
  days: number;
}

type McpTab = 'client' | 'chatgpt' | 'raw';

@Component({
  selector: 'app-token-manager',
  templateUrl: './token-manager.component.html',
  styleUrls: ['./token-manager.component.css']
})
export class TokenManagerComponent implements OnInit, OnDestroy {
  [x: string]: any;

  // Cache for dark mode (avoids relying on Tailwind's `dark:` variant, which
  // conflicts with the legacy Tailwind CDN stylesheets loaded in index.html
  // and would otherwise follow the OS color scheme instead of the app toggle).
  private isDarkModeActive: boolean = false;
  private darkModeObserver: MutationObserver | null = null;

  tokens: any[] = [];
  copyText: string = "Copy";
  loading: boolean = true;

  showCreateModal: boolean = false;
  newTokenName: string = '';
  selectedExpiration: number = 1461; // 4 years in days (default "no expiration")
  creatingToken: boolean = false;
  createError: string = '';

  showDeleteConfirm: boolean = false;
  tokenToDelete: any = null;
  deletingToken: boolean = false;

  // Newly created token (shown once for the user to copy)
  justCreatedToken: any = null;
  justCreatedCopyText: string = 'Copy';

  expirationOptions: ExpirationOption[] = [
    { label: '30 days', days: 30 },
    { label: '90 days', days: 90 },
    { label: '6 months', days: 182 },
    { label: '1 year', days: 365 },
    { label: 'No expiration (4 years)', days: 1461 }
  ];

  // ── Conectar por MCP ────────────────────────────────────────────────
  mcpUrl: string = environment.mcpUrl;
  activeMcpTab: McpTab = 'client';
  mcpCopyText: string = 'Copy';
  mcpUrlCopyText: string = 'Copy';

  constructor(private apiService: ApiService, private cdr: ChangeDetectorRef) {
    this.loadTokens();
  }

  ngOnInit(): void {
    this.updateDarkModeCache();
    this.setupDarkModeListener();
  }

  ngOnDestroy(): void {
    this.darkModeObserver?.disconnect();
  }

  private updateDarkModeCache(): void {
    if (typeof document !== 'undefined') {
      this.isDarkModeActive = document.documentElement.classList.contains('dark');
    }
  }

  private setupDarkModeListener(): void {
    if (typeof document === 'undefined' || typeof MutationObserver === 'undefined') return;
    this.darkModeObserver = new MutationObserver(() => {
      const oldValue = this.isDarkModeActive;
      this.updateDarkModeCache();
      if (oldValue !== this.isDarkModeActive) {
        this.cdr.markForCheck();
      }
    });
    this.darkModeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
  }

  isDarkMode(): boolean {
    return this.isDarkModeActive;
  }

  async loadTokens() {
    this.loading = true;
    try {
      this.tokens = await this.apiService.getTokens({});
    } catch (error) {
      console.error("Error loading tokens:", error);
      this.tokens = [];
    }
    this.loading = false;
  }

  copy(token: string) {
    navigator.clipboard.writeText(token);
    this.copyText = "Copied!";
    setTimeout(() => {
      this.copyText = "Copy";
    }, 1500);
  }

  copyJustCreated() {
    if (this.justCreatedToken) {
      navigator.clipboard.writeText(this.justCreatedToken.token);
      this.justCreatedCopyText = 'Copied!';
      setTimeout(() => {
        this.justCreatedCopyText = 'Copy';
      }, 1500);
    }
  }

  openCreateModal() {
    this.showCreateModal = true;
    this.newTokenName = '';
    this.selectedExpiration = 1461;
    this.createError = '';
    this.justCreatedToken = null;
  }

  closeCreateModal() {
    this.showCreateModal = false;
    this.justCreatedToken = null;
    this.newTokenName = '';
    this.createError = '';
  }

  async createToken() {
    if (!this.newTokenName.trim()) {
      this.createError = 'Please enter a name for the token';
      return;
    }

    this.creatingToken = true;
    this.createError = '';

    try {
      const result = await this.apiService.createToken({
        name: this.newTokenName.trim(),
        expires_in_days: this.selectedExpiration
      });

      if (result.success) {
        this.justCreatedToken = result.token;
        await this.loadTokens();
      } else {
        this.createError = result.message || 'Failed to create token';
      }
    } catch (error: any) {
      this.createError = error?.response?.data?.message || 'Failed to create token';
    }
    this.creatingToken = false;
  }

  openDeleteConfirm(token: any) {
    this.tokenToDelete = token;
    this.showDeleteConfirm = true;
  }

  closeDeleteConfirm() {
    this.showDeleteConfirm = false;
    this.tokenToDelete = null;
  }

  async confirmDelete() {
    if (!this.tokenToDelete) return;

    this.deletingToken = true;
    try {
      await this.apiService.deleteToken(this.tokenToDelete.id);
      await this.loadTokens();
      this.closeDeleteConfirm();
    } catch (error) {
      console.error("Error deleting token:", error);
    }
    this.deletingToken = false;
  }

  maskToken(token: string): string {
    if (!token) return '';
    return token.substring(0, 24) + '••••••••••••••••';
  }

  // ── Conectar por MCP ────────────────────────────────────────────────

  /** Uses the real value right after creating a token; otherwise a placeholder to fill in. */
  get mcpTokenPlaceholder(): string {
    return this.justCreatedToken?.token || 'YOUR_API_TOKEN';
  }

  setMcpTab(tab: McpTab) {
    this.activeMcpTab = tab;
  }

  get mcpClientConfigSnippet(): string {
    return JSON.stringify({
      mcpServers: {
        meetzy: {
          url: this.mcpUrl,
          headers: {
            Authorization: `Bearer ${this.mcpTokenPlaceholder}`
          }
        }
      }
    }, null, 2);
  }

  copyMcpSnippet() {
    navigator.clipboard.writeText(this.mcpClientConfigSnippet);
    this.mcpCopyText = 'Copied!';
    setTimeout(() => { this.mcpCopyText = 'Copy'; }, 1500);
  }

  copyMcpUrl() {
    navigator.clipboard.writeText(this.mcpUrl);
    this.mcpUrlCopyText = 'Copied!';
    setTimeout(() => { this.mcpUrlCopyText = 'Copy'; }, 1500);
  }
}
